/**
 * Time-zone arithmetic for Admin Center → Maintenance windows (CTX-1294).
 *
 * Networking writes its notices in central Mexico time, and the backend writes
 * that same zone into the text customers read. The form, the list and the
 * description preview all have to agree on what "00:00" means, so the
 * conversions live here, once.
 */

export const MEXICO_CITY_TIME_ZONE = "America/Mexico_City";

function parseInstant(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Minutes `timeZone` is ahead of UTC at `instant` (Mexico City: -360).
 *
 * Asked of `Intl` rather than hard-coded: Mexico dropped DST in 2022, and a
 * fixed -6 would be wrong for any window before that change.
 */
export function timeZoneOffsetMinutes(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const field = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  const wallClockAsUtc = Date.UTC(
    field("year"),
    field("month") - 1,
    field("day"),
    field("hour"),
    field("minute"),
    field("second")
  );
  const instantToTheSecond = Math.floor(instant.getTime() / 1000) * 1000;
  return Math.round((wallClockAsUtc - instantToTheSecond) / 60000);
}

const LOCAL_INPUT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/**
 * A `datetime-local` value read as wall-clock time in `timeZone`, as UTC ISO.
 * Null when the value is not a complete `YYYY-MM-DDTHH:mm`.
 *
 * The browser would read the input in the operator's OWN zone; an operator
 * outside Mexico typing the 00:00 from Networking's notice would schedule the
 * window at the wrong hour. Reading it in the notice's zone removes that.
 */
export function zonedInputToUtcIso(value: string, timeZone: string): string | null {
  const match = LOCAL_INPUT.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match.map(Number);
  const asIfUtc = Date.UTC(year, month - 1, day, hour, minute);
  // The offset is taken at the guessed instant, then once more at the
  // corrected one, so a value on the far side of a DST change still lands.
  const firstGuess = asIfUtc - timeZoneOffsetMinutes(new Date(asIfUtc), timeZone) * 60000;
  const corrected = asIfUtc - timeZoneOffsetMinutes(new Date(firstGuess), timeZone) * 60000;
  return new Date(corrected).toISOString();
}

/** The inverse: a UTC instant as a `datetime-local` value in `timeZone`. "" when it does not parse. */
export function utcIsoToZonedInput(iso: string, timeZone: string): string {
  const instant = parseInstant(iso);
  if (!instant) return "";
  const shifted = new Date(instant.getTime() + timeZoneOffsetMinutes(instant, timeZone) * 60000);
  return shifted.toISOString().slice(0, 16);
}

// Same options as the backend's `description.js`, so the preview reads exactly
// like the text that will be stored.
const dayFormat = new Intl.DateTimeFormat("en-US", {
  timeZone: MEXICO_CITY_TIME_ZONE,
  weekday: "short",
  month: "short",
  day: "numeric",
  year: "numeric",
});

const timeFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: MEXICO_CITY_TIME_ZONE,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/**
 * "Tue, Oct 27, 2026, 00:00 – 01:00", spelling out the end day when the
 * window crosses midnight. Always Mexico City time. Null when either end does
 * not parse.
 */
export function formatMexicoCityWindow(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined
): string | null {
  const start = parseInstant(startsAt);
  const end = parseInstant(endsAt);
  if (!start || !end) return null;
  const startDay = dayFormat.format(start);
  const endDay = dayFormat.format(end);
  const endLabel =
    startDay === endDay ? timeFormat.format(end) : `${endDay}, ${timeFormat.format(end)}`;
  return `${startDay}, ${timeFormat.format(start)} – ${endLabel}`;
}

/** The end of a stored record: it keeps a start and a duration, not an end. */
export function endFromDuration(startsAt: string, durationSeconds: number): string | null {
  const start = parseInstant(startsAt);
  if (!start || !Number.isFinite(durationSeconds)) return null;
  return new Date(start.getTime() + durationSeconds * 1000).toISOString();
}
